import { EmbeddedViewRef, Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { AuthState } from '@wilson/auth/core';
import { ShiftTemplate } from '@wilson/interfaces';
import { LocationsStoreService } from '@wilson/locations';
import { ShiftTimelinePreferredSettingsState } from '@wilson/preferred-settings/state';
import { addHours, startOfDay } from 'date-fns';
import {
  NzContextMenuService,
  NzDropdownMenuComponent,
} from 'ng-zorro-antd/dropdown';
import { NzMessageService } from 'ng-zorro-antd/message';
import { firstValueFrom } from 'rxjs';
import { NewAbsenceDropZoneService } from '../../../components/new-absence-drop-zone/new-absence-drop-zone.service';
import { ShiftTemplateHelperService } from '../../../components/shift-template-drop-zone/shift-template-helper.service';
import { NewShiftDropZoneHelperService } from '../../new-shift-drop-zone-component-v2/new-shift-drop-zone-helper.service';
import { ShiftTemplateDropZoneHelperService } from '../../shift-template-drop-zone-v2/shift-template-drop-zone-helper.service';

@Injectable()
export class TimelineUserRowContextMenuWrapperService {
  private store = inject(Store);
  private locationsStoreService = inject(LocationsStoreService);
  private nzMessage = inject(NzMessageService);
  private translateService = inject(TranslateService);
  private newAbsenceDropZoneService = inject(NewAbsenceDropZoneService);
  private newShiftDropZoneHelperService = inject(NewShiftDropZoneHelperService);
  private shiftTemplateHelperService = inject(ShiftTemplateHelperService);
  private shiftTemplateDropZoneHelperService = inject(
    ShiftTemplateDropZoneHelperService,
  );

  private mouseEvent!: MouseEvent;
  private userId!: string;

  createDropDown({
    menuService,
    mouseEvent,
    menu,
    userId,
  }: {
    userId: string;
    menuService: NzContextMenuService;
    mouseEvent: MouseEvent;
    menu: NzDropdownMenuComponent;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }): EmbeddedViewRef<any> {
    this.mouseEvent = mouseEvent;
    this.userId = userId;
    return menuService.create(mouseEvent, menu);
  }

  async createEmptyShift(): Promise<void> {
    const organizationalUnit = this.store.selectSnapshot(
      AuthState.organizationalUnit,
    );

    if (organizationalUnit?.locationId) {
      const location = await firstValueFrom(
        this.locationsStoreService.getLocationFromCache(
          organizationalUnit.locationId as string,
        ),
      ).catch(() => {
        this.nzMessage.error(this.translateService.instant('general.error'));
      });

      if (location) {
        let shiftStartDate =
          this.newShiftDropZoneHelperService.generateDateFromOffsetX(
            this.mouseEvent.offsetX,
          );
        const userId = this.store.selectSnapshot(AuthState.userId);
        if (userId) {
          const isCellView = this.store.selectSnapshot(
            ShiftTimelinePreferredSettingsState.isCellView(userId),
          );
          if (isCellView) {
            shiftStartDate = addHours(startOfDay(shiftStartDate), 8);
          }
          const processResult =
            this.newShiftDropZoneHelperService.handleOnDragOverProcess({
              shiftStartDate,
              offsetX: this.mouseEvent.offsetX,
              location,
              organizationalUnit,
              userId: this.userId,
            });

          if (processResult) {
            this.newShiftDropZoneHelperService.handleOnDropProcess({
              currentDemarcatorRange: processResult.currentDemarcatorRange,
              mockShift: processResult.mockShift,
              mockShiftWithoutActivities:
                processResult.mockShiftWithoutActivities,
              userId: this.userId,
            });
          }
        }
      }
    }
  }

  async createNewAbsence(): Promise<void> {
    const userId = this.store.selectSnapshot(AuthState.userId);
    if (userId) {
      const processResult =
        this.newAbsenceDropZoneService.handleOnDragOverProcess({
          offsetX: this.mouseEvent.offsetX,
          userId: this.userId,
          absenceDurationHours: 24,
          isFullDay: true,
        });
      if (processResult) {
        await this.newAbsenceDropZoneService.handleOnDropProcess({
          currentDemarcatorRange: processResult.currentDemarcatorRange,
          mockAbsence: processResult.mockAbsence,
          userId: this.userId,
        });
      }
    }
  }

  async createShiftFromTemplate(template: ShiftTemplate): Promise<void> {
    this.shiftTemplateHelperService.setShiftTemplate(template);
    const processResult =
      this.shiftTemplateDropZoneHelperService.handleOnDragOverProcess({
        offsetX: this.mouseEvent.offsetX,
        userId: this.userId,
      });

    if (processResult) {
      await this.shiftTemplateDropZoneHelperService.handleOnDropProcess({
        shiftStartDate: processResult.shiftStartDate,
        shiftTemplate: template,
        mockShiftWithoutActivities: processResult.mockShiftWithoutActivities,
        userId: this.userId,
      });
    }
  }
}
